import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonIcons } from '../action-button/models/button-icons.enum';
import { Document } from '../../models/document.model';
import * as moment from 'moment';
import { MatSidenav } from '@angular/material/sidenav';
import { takeUntil, tap } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ToastType } from '../toast/models/toast-type.enum';
import { fadeIn, fadeOut } from 'src/app/animations';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { PageType } from 'src/app/models/enum/page-type.enum';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { JobDocumentsService } from 'src/app/services/sections/job/job-documents.service';
import { DocViewerDocumentDetails } from '../document-viewer/document-viewer.component';

@Component({
	selector: 'document-selection',
	templateUrl: './document-selection.component.html',
	styleUrls: ['../stylesheets/document-viewer.scss', '../stylesheets/job-actions.scss', '../stylesheets/job-main.scss', '../stylesheets/job-inputs.scss', './document-selection.component.scss'],
	animations: [fadeIn(200), fadeOut(200)]
})
export class DocumentSelectionComponent implements OnInit, OnDestroy, AfterViewInit {

	@ViewChild('sidenav', { static: true }) sidenav: MatSidenav;

	public readonly ButtonIcons = ButtonIcons;
	public readonly PageType = PageType;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;

	public moment = moment;

	public chosenItems: Map<number, Document> = new Map<number, Document>();
	public initialChosenItems: Map<number, Document> = new Map<number, Document>();
	public options: Map<number, Document> = new Map<number, Document>();
	public nameEditCollection: Map<number, { name: string, initialName: string }> = new Map<number, { name: string, initialName: string }>();

	public isOpened = false;
	public selectedDocumentDetails: DocViewerDocumentDetails = null;

	public self = this;
	public pageType: PageType;
	public customerPreferences: SettingsPreferences;

	public get chosenItemsKeys(): number[] {
		return Array.from(this.chosenItems.keys());
	}

	public get optionsKeys(): number[] {
		return Array.from(this.options.keys()).sort((first, second) => this.options.get(first).createdOn < this.options.get(second).createdOn ? 1 : -1);
	}

	public documentLoadingUnsubscriber$ = new Subject<void>();

	// State that gets passed to this page and back to the parent page so it won't get reset on "Back"
	private carriedPayload: any;
	private unsubscriber$ = new Subject<void>();

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private activatedRoute: ActivatedRoute,
		private jobDocumentsService: JobDocumentsService
	) {
		const routingData = this.router.getCurrentNavigation().extras.state;

		this.pageType = this.activatedRoute.snapshot.data.pageType;
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences;

		if (routingData?.selectionData) {
			this.initialChosenItems = routingData.selectionData;

			this.chosenItems = new Map<number, Document>();
			Array.from(this.initialChosenItems.keys()).forEach(key => this.chosenItems.set(key, Document.clone(this.initialChosenItems.get(key))));
		}

		if (routingData?.carriedPayload) {
			this.carriedPayload = routingData.carriedPayload;
		}
	}

	ngOnInit(): void {
		const jobDocuments: Document[] = (this.activatedRoute.snapshot.data.jobDocuments || []).map(item => Document.clone(item));
		jobDocuments.forEach(item => this.options.set(item.documentId, item));
		jobDocuments.forEach(item => this.nameEditCollection.set(item.documentId, { name: item.description || item.getName(), initialName: item.description }));
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.isSecondaryLoaderLoading = false;
	}

	ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
		this.documentLoadingUnsubscriber$.next();
		this.documentLoadingUnsubscriber$.complete();
	}

	public toggleSelection(selectedId: number): void {
		if (this.chosenItems.get(selectedId)) {
			this.chosenItems.delete(selectedId);
		} else {
			this.chosenItems.set(selectedId, this.options.get(selectedId));
		}
	}

	public showDocument(documentId: number, documentName: string) {
		this.selectedDocumentDetails = null;

		if (!this.isOpened) {
			this.toggleSlide();
		}

		this.jobDocumentsService
			.getProposalDocument(TokenHelper.getToken(this.route), documentId)
			.pipe(
				takeUntil(this.unsubscriber$),
				takeUntil(this.documentLoadingUnsubscriber$)
			)
			.subscribe((content) => {
				this.selectedDocumentDetails = { documentId, content, documentDownloadName: documentName, documentDisplayName: documentName };
			},() => {
				this.selectedDocumentDetails = null;
			});
	}

	public toggleSlide(): void {
		this.isOpened = !this.isOpened;
		this.sidenav.toggle();
	}

	public submitSelection(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;

		const editedDocuments = [...this.nameEditCollection.keys()].filter(
			documentId => this.nameEditCollection.get(documentId).initialName !== this.nameEditCollection.get(documentId).name
		);

		let action = of(null);

		if (editedDocuments?.length) {
			action = this.jobDocumentsService.changeDocumentListDescription(
				TokenHelper.getToken(this.activatedRoute),
				editedDocuments.map(item => ({
					documentId: item,
					description: this.nameEditCollection.get(item).name
				}))
			).pipe(
				tap(() => {
					editedDocuments.forEach(documentId => {
						if (this.chosenItems.get(documentId)) {
							this.chosenItems.get(documentId).description = this.nameEditCollection.get(documentId).name;
						}
					});
				})
			);
		}

		action.subscribe(
			() => {
				this.navigateWithData('..', this.chosenItems);
				GlobalSettingsService.isSecondaryLoaderLoading = false;
			}, () => {
				ToastService.pushToast({
					type: ToastType.Error,
					icon: 'error',
					text: 'Could not edit all document names!'
				});
				GlobalSettingsService.isSecondaryLoaderLoading = false;
			});
	}

	public moveBack(): void {
		this.navigateWithData('..', this.initialChosenItems);
	}

	public navigateWithData(subpage: string, data?: any): void {
		const state = {} as any;

		if (data) {
			state.selectionData = data;
		}

		if (this.carriedPayload) {
			state.carriedPayload = this.carriedPayload;
		}

		this.router.navigate([subpage], { relativeTo: this.activatedRoute, state });
	}
}
